// The annual report license. Buying it unlocks the report's figures, downloads and saved versions; everything else in
// the organizer is free. The license is a signed token from our server, kept in this browser. Only the token and the
// buyer's email go to and from the server: tax data never does.
import { createContext, useCallback, useContext, useEffect, useState } from "react";

export interface StoredLicense {
  license: string;
  email: string | null;
  taxYear: number;
}

export interface Price {
  amountCents: number;
  earlyBird: boolean;
  regularCents: number;
  earlyBirdEnds: string;
  available: boolean;
  recovery: boolean;
}

export type LicenseStatus = "checking" | "unlocked" | "locked";

export interface LicenseState {
  status: LicenseStatus;
  license: StoredLicense | null;
  /** A one-off message about a purchase that just came back from checkout. */
  notice: { ok: boolean; text: string } | null;
  dismissNotice: () => void;
  /** True when the page was just opened from a finished checkout, so the organizer can open the report. */
  justPurchased: boolean;
  getPrice: () => Promise<Price | null>;
  checkout: () => Promise<string | null>;
  recover: (email: string) => Promise<string>;
  /** Restores licenses from a backup. */
  restore: (list: StoredLicense[]) => void;
}

const key = (taxYear: number) => `ftr.license.${taxYear}`;

export function readLicense(taxYear: number): StoredLicense | null {
  try {
    const raw = localStorage.getItem(key(taxYear));
    if (!raw) return null;
    const v = JSON.parse(raw) as StoredLicense;
    return typeof v.license === "string" && v.taxYear === taxYear ? v : null;
  } catch {
    return null;
  }
}

function writeLicense(v: StoredLicense | null, taxYear: number) {
  try {
    if (v) localStorage.setItem(key(taxYear), JSON.stringify(v));
    else localStorage.removeItem(key(taxYear));
  } catch {
    // Private windows can refuse storage; the license then lasts for this visit only.
  }
}

async function api<T>(path: string, init?: RequestInit): Promise<{ status: number; body: T | null }> {
  const res = await fetch(`/api/${path}`, { ...init, headers: { "content-type": "application/json", ...init?.headers } });
  const body = (await res.json().catch(() => null)) as T | null;
  return { status: res.status, body };
}

/** Today's price from our server, or null when it can't be reached. */
export async function fetchPrice(): Promise<Price | null> {
  try {
    const r = await api<Price>("price");
    return r.status === 200 ? r.body : null;
  } catch {
    return null;
  }
}

const SESSION_RE = /^cs_(test|live)_[A-Za-z0-9]{10,200}$/;

export function useLicenseState(taxYear: number): LicenseState {
  const [license, setLicense] = useState<StoredLicense | null>(() => readLicense(taxYear));
  const [status, setStatus] = useState<LicenseStatus>("checking");
  const [notice, setNotice] = useState<LicenseState["notice"]>(null);
  const [justPurchased, setJustPurchased] = useState(false);

  const keep = useCallback(
    (v: StoredLicense | null) => {
      writeLicense(v, taxYear);
      setLicense(v);
      setStatus(v ? "unlocked" : "locked");
    },
    [taxYear],
  );

  useEffect(() => {
    let live = true;
    const params = new URLSearchParams(window.location.search);
    const purchase = params.get("purchase");
    if (purchase !== null) {
      params.delete("purchase");
      const q = params.toString();
      window.history.replaceState(null, "", window.location.pathname + (q ? `?${q}` : "") + window.location.hash);
    }

    (async () => {
      if (purchase && SESSION_RE.test(purchase)) {
        try {
          const r = await api<StoredLicense & { error?: string }>(`license?session_id=${encodeURIComponent(purchase)}`);
          if (!live) return;
          if (r.status === 200 && r.body?.license) {
            keep({ license: r.body.license, email: r.body.email, taxYear: r.body.taxYear });
            setJustPurchased(true);
            setNotice({ ok: true, text: `Thank you. Your ${taxYear} report is unlocked on this device.` });
            return;
          }
          setNotice({
            ok: false,
            text:
              r.status === 402
                ? "Your payment hasn't gone through yet. If you finished checkout, reload this page in a minute."
                : "We couldn't confirm that purchase. If you were charged, use your email below to get your unlock link.",
          });
        } catch {
          if (live) setNotice({ ok: false, text: "We couldn't reach our server to confirm your purchase. Reload this page to try again." });
        }
      } else if (purchase === "cancelled") {
        setNotice({ ok: true, text: "Checkout was cancelled and nothing was charged." });
      }

      const stored = readLicense(taxYear);
      if (!stored) {
        if (live) setStatus("locked");
        return;
      }
      try {
        const r = await api<{ valid: boolean }>("license/verify", { method: "POST", body: JSON.stringify({ license: stored.license }) });
        if (!live) return;
        // Only a definite "not valid" removes a license. If the server can't check right now, the stored one stands.
        if (r.status === 200 && r.body?.valid === false) keep(null);
        else setStatus("unlocked");
      } catch {
        if (live) setStatus("unlocked");
      }
    })();
    return () => {
      live = false;
    };
  }, [taxYear, keep]);

  const getPrice = useCallback(fetchPrice, []);

  const checkout = useCallback(async () => {
    try {
      const r = await api<{ url?: string }>("checkout", { method: "POST", body: "{}" });
      if (r.status === 200 && r.body?.url) {
        window.location.assign(r.body.url);
        return null;
      }
      return r.status === 503 ? "Buying isn't open yet. Please check back soon." : "Checkout didn't start. Please try again.";
    } catch {
      return "We couldn't reach our server. Check your connection and try again.";
    }
  }, []);

  const recover = useCallback(
    async (email: string) => {
      try {
        const r = await api<{ ok?: boolean }>("license/recover", { method: "POST", body: JSON.stringify({ email }) });
        if (r.status === 200) return `If ${email.trim()} bought the ${taxYear} report, an unlock link is on its way. Open it on this device.`;
        if (r.status === 400) return "That doesn't look like an email address.";
        if (r.status === 429) return "That's a lot of requests. Please wait an hour and try again.";
        return "We couldn't send the email just now. Please try again later.";
      } catch {
        return "We couldn't reach our server. Check your connection and try again.";
      }
    },
    [taxYear],
  );

  const restore = useCallback(
    (list: StoredLicense[]) => {
      for (const v of list) {
        if (typeof v?.license !== "string" || typeof v.taxYear !== "number") continue;
        if (v.taxYear === taxYear) keep(v);
        else writeLicense(v, v.taxYear);
      }
    },
    [taxYear, keep],
  );

  return { status, license, notice, dismissNotice: () => setNotice(null), justPurchased, getPrice, checkout, recover, restore };
}

export const LicenseContext = createContext<LicenseState | null>(null);

export function useLicense(): LicenseState {
  const c = useContext(LicenseContext);
  if (!c) throw new Error("useLicense outside LicenseContext");
  return c;
}

export function dollars(cents: number): string {
  return `$${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`;
}
